import { useEffect } from "react";
import { useRouterState } from "@tanstack/react-router";
import { initAnalytics, installAutoTracking, trackPageView } from "@/lib/analytics";

const INTERACTIONS = ["pointerdown", "keydown", "touchstart", "scroll"] as const;

/**
 * Boots Google Analytics (consent-gated) and reports SPA page views.
 *
 * The gtag bundle is ~170 KB of script that competes with hydration on mobile,
 * so it is loaded on the first real interaction (or after 8s of idling) rather
 * than immediately. Page views fired before that are queued on window.dataLayer
 * and replayed by GA once the tag boots, so no hit is lost.
 */
export function AnalyticsProvider() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const search = useRouterState({ select: (s) => s.location.searchStr });

  useEffect(() => {
    let cleanup: (() => void) | undefined;
    let started = false;

    const start = () => {
      if (started) return;
      started = true;
      teardown();
      initAnalytics();
      cleanup = installAutoTracking();
    };

    const timer = window.setTimeout(start, 8000);
    const teardown = () => {
      window.clearTimeout(timer);
      for (const evt of INTERACTIONS) window.removeEventListener(evt, start);
    };
    for (const evt of INTERACTIONS) {
      window.addEventListener(evt, start, { once: true, passive: true });
    }

    return () => {
      teardown();
      cleanup?.();
    };
  }, []);

  useEffect(() => {
    const id = window.setTimeout(() => trackPageView(`${pathname}${search ?? ""}`), 50);
    return () => window.clearTimeout(id);
  }, [pathname, search]);

  return null;
}
